import React, { useCallback, useEffect, useRef } from 'react'

import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import TransactionStatusCard from '@src/components/TransactionStatusCard'
import { IExchangeData } from '@src/types/exchange'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { useConfigStore } from '@src/zustand_stores/config.store'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useOrderUpdatesStore } from '@src/zustand_stores/orderUpdates.store'
import { useTokensStore } from '@src/zustand_stores/tokens.store'

// Initialize logger for this component
const txLogger = createContextLogger('TransactionNotifications')

/**
 * TransactionNotifications component
 *
 * This component integrates with the orderUpdates store to handle background
 * transaction notifications and displays them using TransactionStatusCard UI.
 *
 * - WebSocket connections track transaction status (handled by orderUpdates store)
 * - This component displays notifications when transactions complete
 * - Uses TransactionStatusCard for consistent UI with the rest of the app
 */
function TransactionNotifications(): JSX.Element {
    const pendingOrderIds = useOrderUpdatesStore(state => state.pendingOrderIds)
    const historyStore = useHistoryStore()
    const successAudioRef = useRef<HTMLAudioElement | null>(null)
    const availableNetworks = useConfigStore(state => state.availableNetworks)
    const { tokens } = useTokensStore()
    const { exchangeId, currentModalId, setCurrentModalId } = useApplicationStore()

    // Helper function to get chain name from chain ID
    const getChainName = (chainId?: number): string => {
        if (!chainId) return 'Unknown'
        return availableNetworks[chainId]?.name || 'Unknown'
    }

    // Helper function to check if token chain matches the data chain
    const checkTokenChainMatch = (data?: Partial<IExchangeData>): boolean => {
        if (!data?.token || !data?.chainId) return false
        return data.token.chainId === data.chainId
    }

    // Track already shown notifications to prevent duplicates
    // Using ref instead of state to avoid re-renders and state update issues
    const shownNotificationsRef = useRef<Set<string>>(new Set())

    // Create audio element for success sound
    useEffect(() => {
        successAudioRef.current = new Audio('/sounds/transaction-complete.mp3')
        return () => {
            if (successAudioRef.current) {
                successAudioRef.current = null
            }
        }
    }, [])

    // Register a custom notification handler to show TransactionStatusCard
    useEffect(() => {
        // Replace the default showCompletionNotification with our custom implementation
        const originalShowCompletionNotification = useOrderUpdatesStore.getState().showCompletionNotification

        useOrderUpdatesStore.setState({
            showCompletionNotification: (exchangeId: string, orderId: string) => {
                // Create a unique key for this notification to prevent duplicates
                const notificationKey = `${exchangeId}:${orderId}`

                // Check if we've already shown this notification to prevent duplicates
                if (shownNotificationsRef.current.has(notificationKey)) {
                    txLogger.info(`Skipping duplicate notification for ${notificationKey}`)
                    return
                }

                // Add to our tracking set right away to prevent race conditions
                shownNotificationsRef.current.add(notificationKey)

                // First call the original implementation to update state and log
                originalShowCompletionNotification(exchangeId, orderId)

                // Then display our custom notification using TransactionStatusCard
                try {
                    const txData = historyStore.getOrder(exchangeId)
                    if (txData) {
                        txLogger.info(`Showing notification for completed transaction: ${exchangeId}:${orderId}`)

                        // Add detailed logging to verify amounts are present
                        // Enhanced logging to better debug token issues
                        txLogger.debug('Transaction data details', {
                            sellAmount: txData.sellData?.value?.toString(),
                            sellToken: txData.sellData?.token?.symbol,
                            sellChain: txData.sellData?.chainId,
                            sellChainName: getChainName(txData.sellData?.chainId),
                            buyAmount: txData.buyData?.value?.toString(),
                            buyToken: txData.buyData?.token?.symbol,
                            buyChain: txData.buyData?.chainId,
                            buyChainName: getChainName(txData.buyData?.chainId),
                            // Check if token info matches chain info from notification
                            sellTokenMatchesChain: checkTokenChainMatch(txData.sellData),
                            buyTokenMatchesChain: checkTokenChainMatch(txData.buyData),
                            exchangeId: txData.exchangeId,
                            orderId,
                        })

                        // Play success sound notification
                        if (successAudioRef.current) {
                            try {
                                successAudioRef.current.currentTime = 0
                                successAudioRef.current.play().catch(err => {
                                    txLogger.error('Error playing notification sound', { error: err })
                                })
                            } catch (audioError) {
                                txLogger.error('Error playing notification sound', { error: audioError })
                            }
                        }

                        // Skip notification if transaction data is incomplete or invalid
                        if (
                            !txData.sellData?.token ||
                            !txData.buyData?.token ||
                            !txData.sellData.value ||
                            !txData.buyData.value
                        ) {
                            txLogger.warn('Skipping notification with incomplete transaction data', {
                                exchangeId,
                                orderId,
                                hasSellToken: Boolean(txData.sellData?.token),
                                hasBuyToken: Boolean(txData.buyData?.token),
                                hasSellValue: Boolean(txData.sellData?.value),
                                hasBuyValue: Boolean(txData.buyData?.value),
                            })
                            return
                        }

                        // Fix token chain mismatch if needed
                        if (!checkTokenChainMatch(txData.sellData) && txData.sellData?.chainId) {
                            txLogger.info('Fixing sell token chain mismatch', {
                                tokenChain: txData.sellData.token.chainId,
                                dataChain: txData.sellData.chainId,
                            })

                            // Try to find a matching token from the tokens store
                            const matchingToken = tokens.find(
                                t =>
                                    t.chainId === txData.sellData?.chainId &&
                                    t.symbol === txData.sellData?.token?.symbol
                            )

                            if (matchingToken) {
                                txData.sellData.token = matchingToken
                                txLogger.info('Found matching sell token for correct chain')
                            }
                        }

                        // Similarly fix buy token chain mismatch
                        if (!checkTokenChainMatch(txData.buyData) && txData.buyData?.chainId) {
                            txLogger.info('Fixing buy token chain mismatch', {
                                tokenChain: txData.buyData.token.chainId,
                                dataChain: txData.buyData.chainId,
                            })

                            // Try to find a matching token from the tokens store
                            const matchingToken = tokens.find(
                                t => t.chainId === txData.buyData?.chainId && t.symbol === txData.buyData?.token?.symbol
                            )

                            if (matchingToken) {
                                txData.buyData.token = matchingToken
                                txLogger.info('Found matching buy token for correct chain')
                            }
                        }

                        // Create a deterministic ID for this toast to prevent duplicates and allow targeted dismissal
                        const toastId = `tx-success-${exchangeId}-${orderId}`

                        // Update the currentModalId to match the completed transaction
                        // This ensures the SwapCompleted component will display in the modal
                        setCurrentModalId(exchangeId)

                        // Show toast with TransactionStatusCard success variant
                        // Using a more robust implementation to avoid closure and toggle issues
                        const ToastContent = React.memo(() => {
                            // Create a local function to properly handle toast closing
                            const handleClose = useCallback(() => {
                                try {
                                    // First try to dismiss by ID (most reliable)
                                    toast.dismiss(toastId)
                                } catch (error) {
                                    txLogger.error('Error dismissing toast by ID', { error })
                                    try {
                                        // Fallback: dismiss all toasts
                                        toast.dismiss()
                                    } catch (fallbackError) {
                                        txLogger.error('Error dismissing all toasts', { error: fallbackError })
                                    }
                                }
                            }, [])

                            return <TransactionStatusCard exchange={txData} onClose={handleClose} isSuccess={true} />
                        })

                        // Set display name for ESLint/React DevTools
                        ToastContent.displayName = 'TransactionSuccessToastContent'

                        // Use the toastId for targeted dismissal
                        toast(<ToastContent />, {
                            toastId,
                            position: 'bottom-right',
                            autoClose: 6000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            pauseOnFocusLoss: false, // Don't pause when window loses focus
                            draggable: true,
                            closeButton: true, // Ensure close button is explicitly enabled
                            // Ensure auto-close works by providing an explicit onClose handler
                            onClose: () => {
                                txLogger.debug(`Auto-closing toast notification for ${exchangeId}:${orderId}`)
                                // Make sure to clear any possible references
                                try {
                                    toast.dismiss(toastId)
                                } catch (err) {
                                    // Silence error - this is just a cleanup operation
                                    // and we don't want to disrupt the UI flow if it fails
                                }
                            },
                        })
                    } else {
                        console.error(`[bg-tx] ❌ Could not find transaction data for ${exchangeId}`)
                    }
                } catch (error) {
                    console.error(`[bg-tx] ❌ Error showing notification:`, error)
                }
            },
        })

        // Cleanup function
        return () => {
            // Restore original implementation when component unmounts
            useOrderUpdatesStore.setState({
                showCompletionNotification: originalShowCompletionNotification,
            })
        }
    }, [])

    // Log when pending orders list changes
    useEffect(() => {
        txLogger.info(`Monitoring ${pendingOrderIds.length} orders for background notifications`)
    }, [pendingOrderIds])

    return <React.Fragment></React.Fragment>
}

export default TransactionNotifications
